// =============================================================================
// Dashboard — CCF Front Office hub. Lists available team tools.
// File: client/src/pages/Dashboard.jsx
// Version: v3.2  ·  Session: CCF SC105  ·  2026-09-08
// Change notes:
//   - Carries the condensed athletic face onto the page masthead, enlarges
//     card labels within the existing height, and gives Skip the exact same
//     content geometry as every active card.
//   - Restores the approved condensed category face and gives every functional
//     glyph the same hunter-green weight and scale as its route glyph.
//   - Rebuilds the page as a compact recruiting-playbook dashboard with
//     four-column category rows and authentic receiver-route glyphs.
//   - Preserves the existing capability gates and tool destinations.
//   - Adds only the approved /plan-builder dashboard entry; SC105 owns the
//     destination page and every plan API, database, and portal surface.
//   - Uses the existing Circle-C asset for the account mark. NotificationBell
//     retains its football control and notification behavior.
// =============================================================================

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase.js';
import { useMe } from '../lib/flux/useMe.js';
import NotificationBell from '../components/NotificationBell.jsx';

const CARD_BASE =
  'group h-full min-h-[8.5rem] border border-[#d3d6d2] bg-white/85 px-4 py-3 text-center ' +
  'shadow-[0_2px_10px_rgba(14,42,32,0.06)] transition-all duration-200';
const PLANE = 'border border-[#d3d6d2] bg-[#ecede8]/75 shadow-[0_8px_28px_rgba(14,42,32,0.05)]';
const GRID = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 p-2 auto-rows-fr';
const CARD_INTERACTIVE =
  'hover:border-hunter hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(14,42,32,0.13)] ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-mid-green';

function Chip({ children }) {
  return (
    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center text-hunter transition-colors [&>svg]:!h-9 [&>svg]:!w-9">
      {children}
    </span>
  );
}

function Pill({ children }) {
  return (
    <span className="absolute right-3 top-3 text-[0.55rem] font-bold uppercase tracking-[0.16em] text-[#767d79]">
      {children}
    </span>
  );
}

const ROUTES = {
  go: { start: 20, line: 'M20 35V7', arrow: 'M16 11l4-4 4 4' },
  slant: { start: 10, line: 'M10 35V27L31 8', arrow: 'M25 8h6v6' },
  out: { start: 11, line: 'M11 35V17H34', arrow: 'M30 13l4 4-4 4' },
  dig: { start: 29, line: 'M29 35V17H6', arrow: 'M10 13l-4 4 4 4' },
  corner: { start: 11, line: 'M11 35V20L32 6', arrow: 'M26 6h6v6' },
  post: { start: 29, line: 'M29 35V20L8 6', arrow: 'M14 6H8v6' },
};

function RouteGlyph({ route }) {
  const shape = ROUTES[route];
  return (
    <svg viewBox="0 0 40 40" className="h-9 w-9 text-hunter" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx={shape.start} cy="35" r="2.2" fill="#f7f5ef" />
      <path d={shape.line} />
      <path d={shape.arrow} />
    </svg>
  );
}

// 20px stroke icons, inline so the pass adds no dependency.
const I = {
  huddler: (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="4.5" r="2" /><circle cx="19.5" cy="12" r="2" /><circle cx="12" cy="19.5" r="2" /><circle cx="4.5" cy="12" r="2" /><path d="m9.5 7-2.5 2.5M17 9.5 14.5 7M14.5 17 17 14.5M7 14.5 9.5 17" />
    </svg>
  ),
  invoice: (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2h12v20l-3-2-3 2-3-2-3 2V2Z" /><path d="M14 7c-1-1-4-.5-4 1.2 0 2 4.5 1 4.5 3.2 0 1.8-3.5 2-4.5 1M12 5.5v9M9 17h6" />
    </svg>
  ),
  profile: (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="5" width="19" height="14" rx="2" /><path d="M6 9h5v6H6zM7 9V7.5h3V9M13.5 9h4M13.5 12h4M13.5 15h3" />
    </svg>
  ),
  plans: (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="5" width="19" height="16" rx="2.5" /><path d="M2.5 10h19M7 3v5M17 3v5" /><circle cx="7" cy="15" r="1" /><circle cx="12" cy="15" r="1" /><circle cx="17" cy="15" r="1" />
    </svg>
  ),
  planBuilder: (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2.5" width="16" height="19" rx="2" /><path d="M7 8h10M8 12h8M9 10.5v3M15 10.5v3M8 18c2-3 6-3 8 0" />
    </svg>
  ),
  campaigns: (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7 12 13l8-5.5" /><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="M17 3h5v5M22 3l-6 6" />
    </svg>
  ),
  deck: (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="12" rx="2" /><path d="M12 16v4M8.5 20h7M7 13V8l4 2 3-4 4 2" />
    </svg>
  ),
  skip: (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 15V10a7 7 0 0 1 14 0v5M5 13H3v6h4v-6H5ZM19 13h2v6h-4v-6h2ZM17 20c-1 1.5-3 2-5 2" /><path d="M9 9h7v5h-4l-3 2V9Z" />
    </svg>
  ),
  team: (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="4" r="2" /><circle cx="5" cy="19" r="2" /><circle cx="12" cy="19" r="2" /><circle cx="19" cy="19" r="2" /><path d="M12 6v5M5 17v-3h14v3M12 11v6" />
    </svg>
  ),
  memberProfile: (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3" /><path d="M3.5 20v-2.5A4.5 4.5 0 0 1 8 13h2a4.5 4.5 0 0 1 4.5 4.5V20M17.5 11.5v6M14.5 14.5h6" />
    </svg>
  ),
  // SQ-S1 — The Call Sheet: a clipboard with a checked line.
  callsheet: (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4V2.5h6V4M8 10l1.5 1.5L12 8M14 10h2M8 16l1.5 1.5L12 14M14 16h2" />
    </svg>
  ),
};

function ToolCard({ onClick, title, blurb, icon, route, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${CARD_BASE} relative ${disabled ? 'cursor-not-allowed select-none opacity-60' : CARD_INTERACTIVE}`}
    >
      {disabled && <Pill>Soon</Pill>}
      <div className="mx-auto flex h-10 items-center justify-center gap-3">
        <Chip>{icon}</Chip><RouteGlyph route={route} />
      </div>
      <div className="mt-1.5 font-display text-[1.6rem] font-black leading-none text-hunter-deep">{title}</div>
      <p className="mt-1.5 text-[0.7rem] font-bold uppercase leading-tight tracking-[0.2em] text-[#707772]">{blurb}</p>
    </button>
  );
}

function SectionHeader({ number, title, cues }) {
  return (
    <div className="flex min-h-14 items-center justify-between gap-4 bg-hunter-deep px-4 py-3 text-white sm:px-6">
      <div className="flex min-w-0 items-center gap-4">
        <span className="font-athletic text-3xl font-black leading-none sm:text-4xl">{number}</span>
        <span className="h-8 w-px bg-silver-a/70" aria-hidden="true" />
        <h2 className="truncate font-athletic text-2xl font-black uppercase tracking-wide sm:text-3xl">{title}</h2>
      </div>
      <span className="hidden text-[0.58rem] font-bold uppercase tracking-[0.28em] text-silver-a/75 md:block">{cues}</span>
    </div>
  );
}

function CapacityCard({ className = '' }) {
  return (
    <div className={`hidden min-h-[9.25rem] items-center justify-center border border-dashed border-[#b9bfbb] bg-white/20 text-center lg:flex ${className}`}>
      <div>
        <span className="mx-auto flex h-8 w-8 items-center justify-center rounded-full border border-[#aeb5b1] text-xl font-light text-[#8b938e]">+</span>
        <span className="mt-2 block text-[0.58rem] font-bold uppercase tracking-[0.25em] text-[#8b938e]">More to come</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  // S83: the resolved capability map, for the Team card's gate. Before the
  // §4 migration lands this still answers correctly — /me resolves from role
  // defaults when the overrides column does not exist yet. If the SERVER has
  // not deployed, can() falls back to the S80 table, which has no team.manage
  // key, so the card hides — correct, because /api/team would 404 anyway.
  const { can } = useMe();
  const canManageTeam = can('team.manage');
  // SQ-S1: same per-card gate as Team. Scouts and lead specialists resolve
  // false by default and the server 403s regardless, so no card for them.
  const canViewCallSheet = can('call_sheet.view');
  const [teamMember, setTeamMember] = useState(null);

  useEffect(() => {
    async function load() {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) return;
      const { data } = await supabase
        .from('team')
        .select('full_name, role')
        .eq('auth_user_id', sessionData.session.user.id)
        .single();
      if (data) setTeamMember(data);
    }
    load();
  }, []);

  async function signOut() {
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#f5f3ed] text-silver-text">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.055]"
        style={{ backgroundImage: 'repeating-linear-gradient(90deg, #0E2A20 0, #0E2A20 1px, transparent 1px, transparent 8.333%), repeating-linear-gradient(0deg, #0E2A20 0, #0E2A20 1px, transparent 1px, transparent 72px)' }}
        aria-hidden="true"
      />
      <header className="relative z-20 border-b border-white/20 bg-hunter-deep text-white">
        <div className="mx-auto flex max-w-[1450px] items-center justify-between gap-4 px-4 py-3 sm:px-7">
          <div className="flex items-center gap-3">
            <span className="font-display text-2xl font-black tracking-tight">CCF</span>
            <span className="hidden h-7 w-px bg-silver-a/45 sm:block" />
            <div className="hidden sm:block">
              <div className="font-display text-xs font-black uppercase tracking-[0.2em]">Coach Cole Football</div>
              <div className="mt-0.5 text-[0.5rem] font-bold uppercase tracking-[0.3em] text-silver-a/65">People · Process · Opportunity</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {teamMember && <span className="hidden text-xs text-silver-a/80 lg:block">Welcome, {teamMember.full_name}</span>}
            <NotificationBell tone="dark" />
            <div className="h-10 w-10 overflow-hidden rounded-full border border-silver-a/60 bg-hunter" title="CCF account">
              <img src="/circle-c-wordmark.png" alt="CCF" className="h-full w-auto max-w-none object-left" />
            </div>
            <button type="button" onClick={signOut} className="ml-1 text-[0.62rem] font-bold uppercase tracking-[0.14em] text-silver-a/75 transition-colors hover:text-white">Sign Out</button>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-[1450px] px-4 pb-12 pt-8 sm:px-7">
        <div className="mb-7 flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
          <div>
            <div className="mb-1 text-[0.62rem] font-bold uppercase tracking-[0.32em] text-hunter">Coach Cole Football</div>
            <h1 className="font-athletic text-5xl font-black uppercase leading-none tracking-tight text-hunter-deep sm:text-7xl lg:text-8xl">CCF Front Office</h1>
          </div>
          <div className="hidden border-l border-[#aeb5b1] pl-5 text-[0.58rem] font-bold uppercase leading-loose tracking-[0.25em] text-[#737a76] lg:block">
            Discipline creates opportunity<br />Play people · Develop potential · Change lives
          </div>
        </div>
        <div className="space-y-3">

          {/* ── SECTION 1 — the recruit's record and the pitch ──
              Jake, SQ-Chrome S1: Profile Editor and Enrollment Presentation. */}
          <div className={PLANE}>
          <SectionHeader number="01" title="Recruit Operations" cues="Identify · Evaluate · Engage · Enroll" />
          <div className={GRID}>

            {/* Profile Editor — active */}
            <ToolCard
              onClick={() => navigate('/recruits')}
              title="Player Profiles"
              blurb="Recruit Information"
              icon={I.profile}
              route="go"
            />

            {/* Enrollment Presentation — active */}
            <ToolCard
              onClick={() => window.open('https://app.ccfrecruiting.com/enrollment-deck/', '_blank', 'noopener,noreferrer')}
              title="Enrollment Presentation"
              blurb="Shared Intelligence"
              icon={I.deck}
              route="slant"
            />

            {/* The Call Sheet — active (SQ-S1). Per-card gated on
                call_sheet.view, the section plane stays ungated. */}
            {canViewCallSheet && (
              <ToolCard
                onClick={() => navigate('/call-sheet')}
                title="The Call Sheet"
                blurb="Service Delivery Tracker"
                icon={I.callsheet}
                route="dig"
              />
            )}

            <ToolCard
              onClick={() => navigate('/plan-builder')}
              title="Plan Builder"
              blurb="Workout & Nutrition Plans"
              icon={I.planBuilder}
              route="corner"
            />
            {!canViewCallSheet && <CapacityCard />}

          </div>
          </div>

          {/* ── SECTION 2 — money and intake ──
              Jake, SQ-Chrome S1: Huddler, Create Invoice, Payment Plans, Skip. */}
          <div className={PLANE}>
          <SectionHeader number="02" title="Business Operations" cues="People · Finance · Compliance · Growth" />
          <div className={GRID}>

            {/* Huddler — active */}
            <ToolCard
              onClick={() => navigate('/huddler')}
              title="Huddler"
              blurb="Custom Recruiting"
              icon={I.huddler}
              route="post"
            />

            {/* Create Invoice — active (Build B) */}
            <ToolCard
              onClick={() => navigate('/invoice')}
              title="Create Invoice"
              blurb="Bill"
              icon={I.invoice}
              route="out"
            />

            {/* Payment Plans — active */}
            <ToolCard
              onClick={() => navigate('/plans')}
              title="Payment Plans"
              blurb="Structure"
              icon={I.plans}
              route="corner"
            />

            {/* Skip — coming soon. NOT a <button>, and deliberately excluded
                from CARD_INTERACTIVE: no lift, no shadow bloom, no chip scale.
                Animating a card that is explicitly not clickable is a lie. */}
            <ToolCard title="Skip" blurb="CCF AI Team Member" icon={I.skip} route="go" disabled />

          </div>
          </div>

          {/* This plane is ungated; only Team Management is capability-gated. */}
          <div className={PLANE}>
            <SectionHeader number="03" title="CCF Systems" cues="Automate · Integrate · Empower" />
            <div className={GRID}>

            {/* Campaigns (FluxEngine) — active. P9a W-7(c): the exact Profile
                Editor card markup. Shown to EVERY role — an RC or scout lands on
                a server-scoped, possibly empty, list, because the server decides
                visibility and the client never computes it.
                Route stays /campaigns: App.jsx documents that deliberately —
                the path matches /recruits and /plans and carries no product
                codename. The CARD is named FluxEngine; the ROUTE is not. */}
            <ToolCard
              onClick={() => navigate('/campaigns')}
              title="Email Campaigns"
              blurb="FluxEngine"
              icon={I.campaigns}
              route="corner"
            />

            <ToolCard
              onClick={() => navigate('/team-member-profile')}
              title="Team Member Profile"
              blurb="Profile · Notifications · Security"
              icon={I.memberProfile}
              route="out"
            />

            {/* CCF Team Member Accounts — active. S83; renamed and moved out
                of the tool grid at S84 per ruling 52. RENDERED ONLY WHEN
                can('team.manage') — a card that 403s on click is worse than
                no card. Per-card gating is still correct; it is the SECTION
                that is ungated. */}
            {canManageTeam && (
              <ToolCard
                onClick={() => navigate('/team')}
                title="CCF Team Management"
                blurb="Accounts & Permissions"
                icon={I.team}
                route="dig"
              />
            )}

            <CapacityCard className={canManageTeam ? '' : 'lg:col-span-2'} />

            </div>
          </div>

        </div>
        <footer className="mt-6 flex items-center justify-between border-t border-[#b9bfbb] pt-4 text-[0.55rem] font-bold uppercase tracking-[0.22em] text-[#777f7a]">
          <span>CCF · Coach Cole Football</span>
          <span className="hidden sm:block">Character · Competition · Future</span>
        </footer>
      </main>

    </div>
  );
}
